import { useCallback, useState } from 'react';
import { isToday, isWithinInterval, isYesterday, subDays } from 'date-fns';

import useToggle from '@lyearn/core/hooks/useToggle';
import { stopEventPropagation } from '@lyearn/core/utils/events';
import { useTranslation } from '@lyearn/i18n';
import { Trash2Outline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Loader, Stack, Tooltip, Typography, useSnackbar } from '@lyearn/ui';
import * as SidebarAccordion from '@/components/Panel/SidebarAccordion';
import { ChatFragment } from '@/features/messages/graphql/fragments/generated/Chat';
import { useAppConfig } from '@/hooks/useAppConfig';
import usePagination from '@/hooks/usePagination';
import { ChannelTypeEnum } from '@/types/schema';

import { useDeleteChannelMutation } from '../../graphql/mutations/generated/deleteChannel';
import { useGetAllAiChannelsQuery } from '../../graphql/queries/generated/getAllAIChannels';
import { ViewMode } from '../../types';
import DeleteModal from '../DeleteModal';

export const Chats = ({
  currentChannel,
  setCurrentChannel,
  setChannelTitle,
  setViewMode,
  onNewChat,
  theGreatReset,
  setIsFreshChat,
  setShowListDrawer,
  search,
  teachModeEnabled,
}: {
  currentChannel: ChatFragment | null;
  onNewChat: () => void;
  setCurrentChannel: React.Dispatch<React.SetStateAction<ChatFragment | null>>;
  setChannelTitle: React.Dispatch<React.SetStateAction<string>>;
  setViewMode: React.Dispatch<React.SetStateAction<ViewMode>>;
  theGreatReset: () => void;
  setIsFreshChat: React.Dispatch<React.SetStateAction<boolean>>;
  setShowListDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  search: string;
  teachModeEnabled: boolean;
}) => {
  const { t } = useTranslation();
  const { workspaceId } = useAppConfig();
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);
  const { showSnackbar, showErrorSnackbar } = useSnackbar();
  const [_, deleteChannel] = useDeleteChannelMutation();
  const [isExpanded, { toggle }] = useToggle(true);
  // const [isHovering, { on, off }] = useToggle(false);
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [deletingChannelId, setDeletingChannelId] = useState<string | null>(null);

  const {
    data: channels,
    isFetchingMore,
    fetchMore,
    isFetching,
    isLoading,
  } = usePagination(
    useGetAllAiChannelsQuery,
    {
      filters: {
        types: [teachModeEnabled ? ChannelTypeEnum.CoachTheCoach : ChannelTypeEnum.ChatWithAi],
        parentId: workspaceId!,
        search,
      },
    },
    {
      edgeKey: 'channels',
      requestPolicy: 'cache-and-network',
      pageSize: -1,
    },
  );

  const handleDeleteChannel = useCallback((channelId: string) => {
    setShowDeletionModal(true);
    setDeletingChannelId(channelId);
    setIsFreshChat(true);
  }, []);

  const onDeleteChannel = useCallback(
    async (channelId: string) => {
      setIsDeletingId(channelId);
      setShowDeletionModal(false);
      setDeletingChannelId(null);
      if (currentChannel && currentChannel.id === channelId) {
        theGreatReset();
      }
      await deleteChannel({ data: { channelIds: [channelId] } })
        .then((res) => {
          setIsDeletingId(null);
          if (res.data?.removeChannels?.success) {
            showSnackbar({
              message: t('Common:KnowledgeBase.ChatDeleted'),
            });
          } else {
            showErrorSnackbar({
              message: t('Common:Error.SomethingWentWrong'),
            });
          }
        })
        .catch(() => {
          setIsDeletingId(null);
          showErrorSnackbar({
            message: t('Common:Error.SomethingWentWrong'),
          });
        });
    },
    [deleteChannel, showErrorSnackbar, showSnackbar, theGreatReset, currentChannel, t],
  );

  const showLoader = isLoading || isFetching;

  const onClickChannel = useCallback(
    (channel: ChatFragment) => {
      theGreatReset();
      setIsFreshChat(false);
      setCurrentChannel(channel);
      setChannelTitle(channel.name);
      setViewMode(ViewMode.Chat);
      setShowListDrawer(false);
    },
    [setChannelTitle, setCurrentChannel, setViewMode, theGreatReset, setShowListDrawer],
  );

  const renderChats = () => {
    if (showLoader) {
      return (
        <Stack className="mx-auto mt-48 mb-0 min-h-[120px] items-center justify-center">
          <Loader size={20} />
        </Stack>
      );
    }

    if (channels.length === 0) {
      if (search.trim().length === 0) {
        return (
          <Stack
            alignItems="center"
            className={cn('px-12 py-[9px] bg-interactive-secondary rounded-8 group cursor-pointer')}
            justifyContent="between"
            onClick={onNewChat}>
            <Typography className="text-text-secondary" numberOfLines={1} variant="label-02">
              {t('Common:KnowledgeBase.NewChat')}
            </Typography>
          </Stack>
        );
      }

      return (
        <Stack className="px-12 py-[9px]">
          <Typography className="text-text-placeholder" variant="label-02">
            {t('Common:KnowledgeBase.NoItems')}
          </Typography>
        </Stack>
      );
    }

    const today: ChatFragment[] = [];
    const yesterday: ChatFragment[] = [];
    const previous7Days: ChatFragment[] = [];
    const previous30Days: ChatFragment[] = [];
    const older: ChatFragment[] = [];

    const now = new Date();

    channels.forEach((chat) => {
      const chatDate = new Date(chat.createdAt);

      if (isToday(chatDate)) {
        today.push(chat);
      } else if (isYesterday(chatDate)) {
        yesterday.push(chat);
      } else if (isWithinInterval(chatDate, { start: subDays(now, 7), end: now })) {
        previous7Days.push(chat);
      } else if (isWithinInterval(chatDate, { start: subDays(now, 30), end: now })) {
        previous30Days.push(chat);
      } else {
        older.push(chat);
      }
    });

    const channelGroups = [
      ...(today.length > 0
        ? [{ title: t('Common:KnowledgeBase.Dates.Today'), channels: today }]
        : []),
      ...(yesterday.length > 0
        ? [{ title: t('Common:KnowledgeBase.Dates.Yesterday'), channels: yesterday }]
        : []),
      ...(previous7Days.length > 0
        ? [{ title: t('Common:KnowledgeBase.Dates.Previous7Days'), channels: previous7Days }]
        : []),
      ...(previous30Days.length > 0
        ? [{ title: t('Common:KnowledgeBase.Dates.Previous30Days'), channels: previous30Days }]
        : []),
      ...(older.length > 0
        ? [{ title: t('Common:KnowledgeBase.Dates.Older'), channels: older }]
        : []),
    ];

    return (
      <>
        {channelGroups.map((group) => (
          <Stack key={group.title} alignItems="start" direction="col" gap={4}>
            <Typography className="ms-12 mt-14" color="text-text-primary" variant="label-03">
              {group.title}
            </Typography>

            <Stack className="w-full" direction="col">
              {group.channels.map((channel) => {
                const isCurrentChannel = channel.id === currentChannel?.id;

                return (
                  <Stack
                    key={channel.id}
                    alignItems="center"
                    className={cn(
                      'px-12 py-[9px] hover:bg-bg-hover rounded-8 group cursor-pointer w-full',
                      isCurrentChannel ? 'bg-interactive-secondary-hover' : '',
                    )}
                    justifyContent="between"
                    onClick={() => onClickChannel(channel)}>
                    <Tooltip title={channel.name}>
                      <Typography
                        className="text-text-secondary"
                        // className={cn(isCurrentChannel ? 'text-text-primary' : 'text-text-secondary')}
                        numberOfLines={1}
                        variant="body-short-02">
                        {channel.name}
                      </Typography>
                    </Tooltip>
                    {isDeletingId === channel.id ? (
                      <Stack className="flex-shrink-0">
                        <Loader size={24} />
                      </Stack>
                    ) : (
                      <Stack className="opacity-0 group-hover:opacity-100 flex-shrink-0">
                        <Tooltip title={t('Common:Actions.Delete')}>
                          <Trash2Outline
                            fillColorToken="icon-primary"
                            height={20}
                            width={20}
                            onClick={(event) => {
                              stopEventPropagation(event);
                              handleDeleteChannel(channel.id);
                            }}
                          />
                        </Tooltip>
                      </Stack>
                    )}
                  </Stack>
                );
              })}
            </Stack>
          </Stack>
        ))}
      </>
    );
  };

  return (
    <Stack direction="col">
      <Stack
        alignItems="center"
        gapX={4}
        justifyContent="between"
        // onMouseEnter={on}
        // onMouseLeave={off}
      >
        <Stack alignItems="center" className="flex-1" gapX={4} role="button" onClick={toggle}>
          <Typography className="pl-12" variant="label-02">
            {/* <Typography className="pl-12 text-text-placeholder" variant="label-caps"> */}
            {t('Common:KnowledgeBase.Chats')}
          </Typography>
          <SidebarAccordion.Chevron isExpanded={isExpanded} />
        </Stack>

        {/* {isHovering ? (
          <Tooltip title={t('Common:KnowledgeBase.StartANewChat')}>
            <LinkButton onClick={onNewChat}>
              <PlusOutline />
            </LinkButton>
          </Tooltip>
        ) : null} */}
      </Stack>

      <Stack className="mt-8" direction="col" gapY={2}>
        {!isExpanded && currentChannel ? (
          <Stack
            alignItems="center"
            className="px-12 py-[9px] bg-interactive-secondary-hover rounded-8 group cursor-pointer"
            justifyContent="between">
            <Typography className="text-text-secondary" numberOfLines={1} variant="label-02">
              {currentChannel.name}
            </Typography>

            {isDeletingId === currentChannel.id ? (
              <Stack className="flex-shrink-0">
                <Loader size={24} />
              </Stack>
            ) : (
              <Stack className="opacity-0 group-hover:opacity-100 flex-shrink-0">
                <Tooltip title={t('Common:Actions.Delete')}>
                  <Trash2Outline
                    fillColorToken="icon-primary"
                    height={20}
                    width={20}
                    onClick={(event) => {
                      stopEventPropagation(event);
                      handleDeleteChannel(currentChannel.id);
                    }}
                  />
                </Tooltip>
              </Stack>
            )}
          </Stack>
        ) : null}
        <SidebarAccordion.Main isExpanded={isExpanded}>{renderChats()}</SidebarAccordion.Main>
      </Stack>

      <DeleteModal
        description={t('Common:KnowledgeBase.DeleteChatDescription')}
        isSubmitting={false}
        open={showDeletionModal}
        title={t('Common:KnowledgeBase.DeleteChatTitle')}
        onClose={() => setShowDeletionModal(false)}
        onDelete={() => onDeleteChannel(deletingChannelId!)}
      />
    </Stack>
  );
};
